import { authStore } from '@store/AuthStore'
import { i18nStore, t } from '@store/I18nStore'
import type { BillingAccessStore } from '@store/BillingAccessStore'
import {
  EMAIL_NOT_VERIFIED_CODE,
  EMAIL_VERIFICATION_PROFILE_PATH,
  emailVerificationStore,
} from '@store/EmailVerificationStore'

const SESSION_EXPIRED_MESSAGE_KEY = 'auth.sessionExpiredMessage'
/** Stored instead of the raw backend text when the JWT simply ran out (401 TOKEN_EXPIRED). */
export const SESSION_EXPIRED_MARKER = 'auth.session.expired'

/** Remember "the session ran out" for the login page (first reason wins). */
export function markSessionExpired(): void {
  try {
    if (!sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, SESSION_EXPIRED_MARKER)
    }
  } catch {}
}

/**
 * Only an expired ACCESS token is worth a refresh + retry. With an explicit `code` the code
 * decides (TOKEN_REVOKED / SESSION_EXPIRED / REFRESH_* are final — no refresh for those);
 * the text match is a fallback for bodies without a code.
 */
export function isTokenExpiredPayload(data: Record<string, unknown> | null): boolean {
  if (!data) return false
  const code = typeof data.code === 'string' ? data.code.trim().toUpperCase() : ''
  if (code) return code === 'TOKEN_EXPIRED' || code === 'JWT_EXPIRED'
  const text = `${typeof data.error === 'string' ? data.error : ''} ${typeof data.message === 'string' ? data.message : ''}`
  return /token expired|jwt expired|expired token/i.test(text)
}
const UNAUTHORIZED_TOAST_COOLDOWN_MS = 4000

/** Centrifugo terminal disconnect codes — https://centrifugal.dev/docs/server/codes#terminal-disconnect-codes */
export const CENTRIFUGE_SESSION_RESET_CODE = 3503
export const CENTRIFUGE_ACCOUNT_BLOCKED_CODE = 3507
export const SESSION_RESET_REASON = 'SESSION_RESET'
export const ACCOUNT_BLOCKED_REASON = 'ACCOUNT_BLOCKED'

export type ForcedLogoutReason = 'blocked' | 'session-reset'

let unauthorizedHandlingInProgress = false
let lastUnauthorizedHandledAt = 0
let lastSubscriptionExpiredHandledAt = 0
let forcedLogoutInProgress = false

function tokenMatches(value: unknown, token: string): boolean {
  const raw = String(value ?? '').trim()
  if (!raw) return false
  return raw.toUpperCase().includes(token.toUpperCase())
}

function numericCode(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^\d+$/.test(value.trim())) return Number(value.trim())
  return null
}

export function forcedLogoutReasonFromDisconnect(
  code?: unknown,
  reason?: unknown,
): ForcedLogoutReason | null {
  if (
    numericCode(code) === CENTRIFUGE_ACCOUNT_BLOCKED_CODE ||
    tokenMatches(code, ACCOUNT_BLOCKED_REASON) ||
    tokenMatches(reason, ACCOUNT_BLOCKED_REASON)
  ) {
    return 'blocked'
  }
  if (
    numericCode(code) === CENTRIFUGE_SESSION_RESET_CODE ||
    tokenMatches(code, SESSION_RESET_REASON) ||
    tokenMatches(reason, SESSION_RESET_REASON)
  ) {
    return 'session-reset'
  }
  return null
}

export function loginPathForForcedLogout(reason: ForcedLogoutReason): string {
  return reason === 'blocked' ? '/login?reason=blocked' : '/login?reason=session-reset'
}

function isRawLogoutCode(message: string): boolean {
  const upper = message.trim().toUpperCase()
  return upper === ACCOUNT_BLOCKED_REASON || upper === SESSION_RESET_REASON
}

/** Clear local auth and hard-redirect to login. Idempotent across many parallel 403s. */
export function forceSessionLogout(reason: ForcedLogoutReason, message?: string): boolean {
  if (forcedLogoutInProgress) return true
  forcedLogoutInProgress = true
  const text = typeof message === 'string' ? message.trim() : ''
  try {
    if (text && !isRawLogoutCode(text) && !sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, text)
    }
  } catch {}
  authStore.logout({ skipRemote: true })
  if (typeof window === 'undefined') return true
  const path = loginPathForForcedLogout(reason)
  if (`${window.location.pathname}${window.location.search}` !== path) {
    window.location.replace(path)
  }
  return true
}

function disconnectFields(ctx: unknown): { code?: unknown; reason?: unknown } {
  if (!ctx || typeof ctx !== 'object') return {}
  const rec = ctx as Record<string, unknown>
  const nested =
    rec.error && typeof rec.error === 'object' && !Array.isArray(rec.error)
      ? (rec.error as Record<string, unknown>)
      : null
  return {
    code: rec.code ?? nested?.code,
    reason: rec.reason ?? rec.message ?? nested?.message ?? nested?.reason,
  }
}

/** Logout on Centrifugo 3507 ACCOUNT_BLOCKED / 3503 SESSION_RESET (code or reason). */
export function handleCentrifugeForcedDisconnect(ctx: unknown): boolean {
  const { code, reason } = disconnectFields(ctx)
  const forced = forcedLogoutReasonFromDisconnect(code, reason)
  if (!forced) return false
  return forceSessionLogout(forced, typeof reason === 'string' ? reason : undefined)
}

function forcedLogoutReasonFromPayload(data: Record<string, unknown> | null): ForcedLogoutReason | null {
  if (!data) return null
  const nested = asRecord(data.data)
  return forcedLogoutReasonFromDisconnect(
    data.code ?? nested?.code,
    data.reason ?? nested?.reason,
  )
}

function softNavigate(path: string): void {
  if (typeof window === 'undefined') return
  if (window.location.pathname === path) return
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

async function parseApiErrorBody(response: Response): Promise<Record<string, unknown> | null> {
  try {
    const clone = response.clone()
    const data = await clone.json()
    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null
  } catch {
    return null
  }
}

function pickApiErrorText(data: Record<string, unknown> | null): string {
  const message = typeof data?.message === 'string' ? data.message.trim() : ''
  if (message) return message
  const error = typeof data?.error === 'string' ? data.error.trim() : ''
  return error
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export class EmailNotVerifiedError extends Error {
  readonly code = EMAIL_NOT_VERIFIED_CODE

  constructor(message?: string) {
    super(message?.trim() || t('auth.confirm_email.title'))
    this.name = 'EmailNotVerifiedError'
  }
}

/** True when the API body is the email-confirmation restriction (any HTTP status). */
export function isEmailNotVerifiedPayload(data: unknown): boolean {
  const root = asRecord(data)
  if (!root) return false
  const nested = asRecord(root.data)
  const code = String(root.code ?? nested?.code ?? '').trim()
  return code === EMAIL_NOT_VERIFIED_CODE
}

function readBooleanFlag(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

/** `emailVerified` from login (`data.emailVerified` or top-level). */
export function readEmailVerifiedFlag(data: unknown): boolean | undefined {
  const root = asRecord(data)
  if (!root) return undefined
  const nested = asRecord(root.data)
  return (
    readBooleanFlag(nested?.emailVerified) ??
    readBooleanFlag(root.emailVerified) ??
    readBooleanFlag(nested?.email_verified) ??
    readBooleanFlag(root.email_verified)
  )
}

/** True when the signed-in operator still has to confirm email. */
export function isSessionEmailUnverified(): boolean {
  if (emailVerificationStore.blocked) return true
  return authStore.currentUser?.emailVerified === false
}

/** Stay signed in; send the operator to profile until they confirm email. */
export function applyEmailNotVerifiedRestriction(data?: Record<string, unknown> | null): void {
  emailVerificationStore.apply({
    message: pickApiErrorText(data ?? null) || t('auth.confirm_email.title'),
  })
  const user = authStore.currentUser
  if (user && user.emailVerified !== false) {
    authStore.setUser({ ...user, emailVerified: false })
  }
  softNavigate(EMAIL_VERIFICATION_PROFILE_PATH)
}

/**
 * Billing access store, handed over by the root store at start-up. Not imported here: it
 * reaches this module through billingApi, and a dynamic import only made Vite complain.
 */
let billingAccess: BillingAccessStore | null = null

export function registerBillingAccess(store: BillingAccessStore): void {
  billingAccess = store
}

async function refreshBillingAfterEmailVerified(): Promise<void> {
  const base = (i18nStore.apiBaseUrl || '').replace(/\/$/, '')
  if (!billingAccess || !base || !authStore.accessToken) return
  await billingAccess.refresh(base, authStore.authHeader, { force: true })
}

/** Keep auth + realtime in sync after /api/settings/profile (or similar). */
export async function syncEmailVerifiedState(verified: boolean): Promise<void> {
  const user = authStore.currentUser
  const wasUnverified = isSessionEmailUnverified()
  if (user && user.emailVerified !== verified) {
    authStore.setUser({ ...user, emailVerified: verified })
  }
  if (!verified) {
    applyEmailNotVerifiedRestriction()
    return
  }
  emailVerificationStore.clearAndReconnect()
  if (wasUnverified) {
    await refreshBillingAfterEmailVerified()
  }
}

/** After POST /api/auth/verify-email: flag, entitlements refresh, then realtime can connect. */
export async function completeEmailVerification(): Promise<void> {
  const user = authStore.currentUser
  if (user && user.emailVerified !== true) {
    authStore.setUser({ ...user, emailVerified: true })
  }
  emailVerificationStore.clearAndReconnect()
  await refreshBillingAfterEmailVerified()
}

/** Reads `message` / `error` from an API error body without consuming the original response. */
export async function readApiErrorMessage(response: Response, fallback: string): Promise<string> {
  const data = await parseApiErrorBody(response)
  return pickApiErrorText(data) || fallback
}

async function handleForcedLogoutFromResponse(response: Response): Promise<boolean> {
  const data = await parseApiErrorBody(response)
  const forced = forcedLogoutReasonFromPayload(data)
  if (!forced) return false
  forceSessionLogout(forced, pickApiErrorText(data))
  return true
}

async function handleUnauthorizedSideEffects(response: Response): Promise<void> {
  const data = await parseApiErrorBody(response)
  const forced = forcedLogoutReasonFromPayload(data)
  if (forced) {
    forceSessionLogout(forced, pickApiErrorText(data))
    return
  }

  // "Token expired" is not an error the operator did anything about — show the login page
  // with a calm "session ran out, sign in again" banner instead of a red raw-text toast.
  // (The fetch interceptor already tried /api/auth/refresh + one retry before this 401 got
  // here; while a restore is still running or the refresh failed on the network, keep the
  // session — the next request / timer tick will try again.)
  const tokenExpired = isTokenExpiredPayload(data)
  if (tokenExpired && authStore.keepSessionDespite401()) return
  const msg = tokenExpired
    ? SESSION_EXPIRED_MARKER
    : (typeof data?.error === 'string' && data.error) ||
      (typeof data?.message === 'string' && data.message) ||
      'auth.session.replaced'

  try {
    // Keep the first reason if multiple 401s happen.
    if (!sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)) {
      sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, msg)
    }
  } catch {}

  // Skip remote /api/auth/logout: token may already be invalidated and can cause duplicate 401 handling.
  authStore.logout({ skipRemote: true })
  softNavigate('/login')
}

async function handleEmailNotVerifiedSideEffects(response: Response): Promise<void> {
  const data = await parseApiErrorBody(response)
  if (!isEmailNotVerifiedPayload(data)) return
  applyEmailNotVerifiedRestriction(data)
}

async function handleSubscriptionExpiredSideEffects(response: Response): Promise<void> {
  const data = await parseApiErrorBody(response)
  const code = typeof data?.code === 'string' ? data.code : ''
  if (code && code !== 'SUBSCRIPTION_EXPIRED') return

  const details = data?.details
  const detailsAccess =
    details && typeof details === 'object'
      ? (details as { access?: { reason?: unknown } }).access
      : undefined
  const reason =
    (typeof detailsAccess?.reason === 'string' && detailsAccess.reason) ||
    (typeof data?.message === 'string' && data.message) ||
    null

  const billingAccessStore = billingAccess
  if (!billingAccessStore) return
  if (billingAccessStore.awaitingEmailVerification || isSessionEmailUnverified()) return
  billingAccessStore.applyExpired({
    code: code || 'SUBSCRIPTION_EXPIRED',
    reason,
    details,
  })
}

/**
 * Handles 401 Unauthorized (logout), ACCOUNT_BLOCKED / SESSION_RESET on any error
 * status (not only 401 — backend often sends 403), 402 SUBSCRIPTION_EXPIRED, and
 * EMAIL_NOT_VERIFIED (403/422 — stay signed in, send to profile).
 * @returns true if the caller should stop execution
 */
export const handleUnauthorized = (response: Response): boolean => {
  if (response.status === 401) {
    const now = Date.now()
    if (unauthorizedHandlingInProgress || forcedLogoutInProgress) return true
    if (now - lastUnauthorizedHandledAt < UNAUTHORIZED_TOAST_COOLDOWN_MS) return true

    unauthorizedHandlingInProgress = true
    lastUnauthorizedHandledAt = now
    void handleUnauthorizedSideEffects(response).finally(() => {
      unauthorizedHandlingInProgress = false
    })
    return true
  }

  if (response.status === 402) {
    const now = Date.now()
    if (now - lastSubscriptionExpiredHandledAt > 400) {
      lastSubscriptionExpiredHandledAt = now
      void handleSubscriptionExpiredSideEffects(response)
    }
    return true
  }

  if (!response.ok) {
    void handleForcedLogoutFromResponse(response)
  }

  if (response.status === 403 || response.status === 422) {
    void handleEmailNotVerifiedSideEffects(response)
  }

  return false
}
